'use client'

import { addDays } from '@/components/coverage/CoverageSection'
import usePeriodView from '@/components/coverage/usePeriodView'
import SubscribeCard from '@/components/now/SubscribeCard'
import CoverageBlock from '@/components/ui/CoverageBlock'
import PeriodPills from '@/components/ui/PeriodPills'
import { STORYLINES_VIEW_KEY, TOPIC_PERIODS } from '@/constants/topics'
import Link from 'next/link'
import { labelOf, longDate, shortDate, sideOfKey, totalOf } from './format'
import StorylineAside from './StorylineAside'
import StorylineBody from './StorylineBody'

// A storyline's page (the identity mocks, 2026-11-01): a centred masthead
// (where it sits in its run, the title, one sentence of what it holds), the
// whole storyline's articles by side as three big counts over one bar with
// Pano's label, then the day-by-day chart and the chapters with the side
// column (StorylineBody). It replaced the period menu, the share control,
// the "← All storylines" link and the newsletter ask of the 2026-10-24
// layout: this page is for reading the saga through.
//
// The time window (Sam, 2026-11-01, "like the topic page"): the topic
// page's pill and its default (the past week, or the shortest window with a
// chapter in it), plus "All" for the whole run. The chart shows every day of
// the window, quiet ones too. Everything under the masthead follows it:
// the coverage, the chart, the chapters, the outlets and the people and
// places. The masthead keeps describing the whole storyline.

// A day with at least a story's worth of articles (the pipeline's eight)
// can swing the other way from the whole; fewer is noise.
const SWING_MIN = 8
// One array for the page's life: usePeriodView re-reads the address when
// its `periods` change, so a fresh array each render snapped the pill back.
const OPTIONS = [
  ...TOPIC_PERIODS.map((p) => ({ id: p.id, label: p.label })),
  { id: 'all', label: 'All' },
]

const sumWings = (days) =>
  days.reduce(
    (s, d) => ({
      left: s.left + (d.wings?.left || 0),
      center: s.center + (d.wings?.center || 0),
      right: s.right + (d.wings?.right || 0),
    }),
    { left: 0, center: 0, right: 0 }
  )

export default function StorylineProfile({
  storyline,
  today,
  isGuest = false,
}) {
  const {
    days: allDays = [],
    lean: wholeLean,
    outlets: allOutlets = [],
    outletsByPeriod = {},
    topics = [],
    slug,
    title,
  } = storyline
  const first = allDays[0]
  const last = allDays[allDays.length - 1]
  // Oldest first, as the service groups them; same-day duplicates already
  // read as one.
  const allChapters = allDays.flatMap((d) => d.chapters)
  const wholeArticles = totalOf(wholeLean)
  const wholeLabel = labelOf(wholeLean)

  const fromOf = (id) =>
    addDays(today, -(TOPIC_PERIODS.find((p) => p.id === id).days - 1))
  const [period, setPeriod] = usePeriodView({
    initial: TOPIC_PERIODS.find((p) => last.day >= fromOf(p.id))?.id || 'all',
    periods: OPTIONS,
    listPath: '/storylines',
    viewKey: STORYLINES_VIEW_KEY,
  })
  const all = period === 'all'
  const periodDays = all
    ? null
    : TOPIC_PERIODS.find((p) => p.id === period).days
  const days = all ? allDays : allDays.filter((d) => d.day >= fromOf(period))
  const lean = all ? wholeLean : sumWings(days)
  const chapters = days.flatMap((d) => d.chapters)
  const outlets = all ? allOutlets : outletsByPeriod[period] || []
  const articles = totalOf(lean)
  const label = labelOf(lean)
  // "Single days swung hard": a day whose label points the other way from
  // the shown coverage's (or points a way when that doesn't).
  const whole = sideOfKey(label?.key)
  const swung = days.some((d) => {
    if (d.total < SWING_MIN) return false
    const side = sideOfKey(labelOf(d.wings)?.key)
    return side && side !== whole
  })
  const firstChapter = allChapters[0]
  const latestChapter = allChapters[allChapters.length - 1]
  const sameChapter =
    !latestChapter || firstChapter.title === latestChapter.title
  const plural = (n, one, many) =>
    `${n.toLocaleString()} ${n === 1 ? one : many}`

  return (
    <article className="page-wide pb-16">
      <header className="pt-[22px] sm:pt-10 sm:text-center">
        <p className="dateline text-[11px] sm:text-xs">
          <Link href="/storylines" className="hidden underline sm:inline">
            Storylines
          </Link>
          <span className="sm:hidden">Storyline</span> · since{' '}
          <span className="sm:hidden">{shortDate(first.day)}</span>
          <span className="hidden sm:inline">{longDate(first.day)}</span>
        </p>
        <h1 className="mb-2.5 mt-2 font-serif text-[34px] font-medium leading-[1.06] sm:mb-3.5 sm:mt-3 sm:text-[60px] sm:leading-[1.02]">
          {title}
        </h1>
        <p className="font-serif text-[17px] leading-normal text-secondary-foreground sm:mx-auto sm:max-w-[680px] sm:text-[19px]">
          <span className="hidden sm:inline">
            {wholeArticles.toLocaleString()} articles
            {sameChapter ? (
              <> since {longDate(first.day)}.</>
            ) : (
              <>
                , from &ldquo;{firstChapter.title}&rdquo; to &ldquo;
                {latestChapter.title}&rdquo;.
              </>
            )}
          </span>
          <span className="sm:hidden">
            {wholeArticles.toLocaleString()} articles.
            {wholeLabel && (
              <> Pano rates the whole storyline {wholeLabel.text}.</>
            )}
          </span>
        </p>
        <PeriodPills
          options={OPTIONS}
          value={period}
          onChange={setPeriod}
          className="mt-5 sm:mx-auto"
        />
      </header>

      {/* The coverage block, the same one a story and a topic open with,
          for the window the pill picks. */}
      {articles === 0 ? (
        <p className="mt-5 border-t border-foreground pt-6 text-center font-sans text-[13px] text-muted-foreground sm:mt-9">
          No news in the last {periodDays} days. The latest was{' '}
          {longDate(last.day)}.
        </p>
      ) : (
        <CoverageBlock
          counts={lean}
          articles={
            all
              ? undefined
              : `Last ${periodDays} days · ${plural(articles, 'article', 'articles')}`
          }
          label={label?.text}
          note={
            swung
              ? 'Some days leaned much further one way. See the chart.'
              : null
          }
          className="mt-5 sm:mt-9"
        />
      )}

      {days.length > 0 && (
        <StorylineBody
          chapters={chapters}
          days={days}
          slug={slug}
          from={all ? first.day : fromOf(period)}
          to={today}
          aside={
            <StorylineAside
              outlets={outlets}
              topics={topics}
              days={days}
              slug={slug}
            />
          }
        />
      )}

      {/* The newsletter ask, once, after the chapters (2026-11-01): a
          reader following a running story is who a daily email serves.
          Guests only. */}
      {isGuest && (
        <SubscribeCard location="storyline_end" standalone className="mt-12" />
      )}
    </article>
  )
}
